import type {
  PoleImageType,
  PoleAttachmentType,
  VerificationTaskType,
  ReferencePoint,
  Coordinate2D,
} from '../types';

/**
 * Class representing a pole image with reference lines and scaling data
 */
export class PoleImage implements PoleImageType {
  id: string;
  image: string;
  type: string;
  scalingData?: string;
  referenceLine: {
    top: ReferencePoint;
    bottom: ReferencePoint;
  } | null;
  wasAutoCaptured?: string;
  wasCapturedInSafeZone?: string;

  constructor(data: PoleImageType) {
    this.id = data.id;
    this.image = data.image || '';
    this.type = data.type || '';
    this.scalingData = data.scalingData;
    this.referenceLine = data.referenceLine ? {
      top: {
        id: data.referenceLine.top.id,
        pixelCoordinates: data.referenceLine.top.pixelCoordinates,
        worldCoordinates: data.referenceLine.top.worldCoordinates
      },
      bottom: {
        id: data.referenceLine.bottom.id,
        pixelCoordinates: data.referenceLine.bottom.pixelCoordinates,
        worldCoordinates: data.referenceLine.bottom.worldCoordinates
      }
    } : null;
    this.wasAutoCaptured = data.wasAutoCaptured;
    this.wasCapturedInSafeZone = data.wasCapturedInSafeZone;
  }

  /**
   * Calculate X coordinate for a given Y on the reference line
   */
  calculateXforY(y: number, bottomPoint: Coordinate2D, topPoint: Coordinate2D): number {
    const slope = (topPoint.y - bottomPoint.y) / (topPoint.x - bottomPoint.x);
    const yIntercept = bottomPoint.y - slope * bottomPoint.x;
    return (y - yIntercept) / slope;
  }
}

/**
 * Class representing a pole attachment with position and metadata
 */
export class PoleAttachment implements PoleAttachmentType {
  id: string;
  type: string;
  height: number;
  classification: string;
  related_task_id: string;
  owner: string;
  inventoryClass: {
    name: string;
    subcategories: Array<{
      name: string;
      value: string;
    }>;
  };
  images: Array<{
    imageId: string;
    point: {
      x: number;
      y: number;
    }
  }>;
  relatedGuys?: string[];
  relatedSpans?: string[];
  isManualMeasurement?: string;
  manualHeight?: number;
  calculatedHeight?: number;
  distanceFromPoleTop?: number;
  heading?: number | null;

  constructor(data: PoleAttachmentType) {
    this.id = data.id;
    this.type = data.type;
    this.height = data.height;
    this.classification = data.classification || '';
    this.related_task_id = data.related_task_id || '';
    this.owner = data.owner || '';
    this.inventoryClass = data.inventoryClass || { name: '', subcategories: [] };
    this.images = data.images || [];
    this.relatedGuys = data.relatedGuys;
    this.relatedSpans = data.relatedSpans;
    this.isManualMeasurement = data.isManualMeasurement;
    this.manualHeight = data.manualHeight;
    this.calculatedHeight = data.calculatedHeight;
    this.distanceFromPoleTop = data.distanceFromPoleTop;
    this.heading = data.heading;
    
    // If manual measurement, use manualHeight as the height
    if (this.isManualMeasurement === "true" && this.manualHeight !== undefined) {
      this.height = this.manualHeight;
    }
  }

  /**
   * Update the position of an attachment on a specific image
   */
  updatePosition(imageId: string, x: number, y: number): void {
    const imageIndex = this.images.findIndex(img => img.imageId === imageId);
    if (imageIndex !== -1) {
      this.images[imageIndex] = {
        ...this.images[imageIndex],
        point: { x, y }
      };
    }
  }

  /**
   * Update a measurement value
   */
  updateMeasurement(key: string, value: number): void {
    if (key in this) {
      (this as any)[key] = value;
    }
  }
}

/**
 * Class representing a verification task
 */
export class VerificationTask implements VerificationTaskType {
  id: string;
  inspectionPhase: string;
  inventoryClass: {
    name: string;
    subcategories: Array<{
      name: string;
      value: string;
    }>
  }
  measurementId: string;
  status: string;

  constructor(data: VerificationTaskType) {
    this.id = data.id;
    this.inspectionPhase = data.inspectionPhase;
    this.inventoryClass = data.inventoryClass;
    this.measurementId = data.measurementId;
    this.status = data.status;
  }
}